import { getRootOffset } from '../../../helpers';

import Gallery from '../gallery/index';
import Hero from './hero';
import Split from './split';
import SplitSlider from './split-slider';

export default class Home {

	selector = 'home';

	hero;

	constructor({
		modules,
		scene,
		clock,
		sizes,
		geometry,
		isPreload
	}) {

		const container = document.querySelector(`[data-uri="${this.selector}"]`);

		this.el = {
			container,
			heroes: container.querySelectorAll('[data-gl-hero]'),
			splits: container.querySelectorAll('[data-gl-split]'),
			sliders: container.querySelectorAll('[data-gl-slider]'),
			galleries: container.querySelectorAll('[data-gl-gallery]')
		};

		this.modules = modules;
		this.scene = scene;
		this.clock = clock;
		this.sizes = sizes;
		this.geometry = geometry;

		this.isPreload = isPreload;

		this.scroll = null;

		// Component assemblies
		this.heroes = [];
		this.galleries = [];
		this.splits = [];
		this.sliders = [];

		// GL components
		this.glHeroes = [];
		this.glSplits = [];
		this.glSplitSliders = [];
		this.glGalleries = [];

		// TODO: Store in APPDATA
		this.types = {
			hero: 'hero',
			split: 'splitsNext',
			splitSlider: 'splitsFeature',
			gallery: 'gallery'
		};
	}

	init() {
		this.createComponents();
		this.createMeshes();
	}

	parse(asset) {
		const el = asset.querySelector('img');
		const ref = asset.querySelector('[data-gl-ref]');
		const offsets = getRootOffset(asset);

		return {
			el,
			ref,
			cache: asset,
			index: asset.index,
			parentUid: asset.parentUid,
			uid: asset.dataset.glUid,
			type: asset.dataset.glType,
			bounds: {
				imageWidth: ref.offsetWidth,
				imageHeight: ref.offsetHeight,
				width: asset.offsetWidth,
				height: asset.offsetHeight,
				top: offsets.top,
				left: offsets.left
			}
		};
	}

	componentize(parts, assembly) {
		for (const part of parts) {
			const { glParentUid, glType } = part.dataset;
			const images = part.querySelectorAll('[data-gl-image]');
			const controls = part.querySelectorAll('[data-gl-control]');
			const assets = [];

			for (const [i, entry] of images.entries()) {
				entry.index = i;
				entry.parentUid = glParentUid;
				const image = this.parse(entry);
				const texture = this.mapTexture(image);
				texture.imageIndex = entry.index;
				assets.push({ image, texture });
			}

			assembly.push({
				el: part,
				type: glType,
				assets,
				sizes: this.sizes,
				controls: controls.length ? controls : null
			});
		}
	}

	createComponents() {
		this.componentize(this.el.heroes, this.heroes);
		this.componentize(this.el.splits, this.splits);
		this.componentize(this.el.sliders, this.sliders);
		this.componentize(this.el.galleries, this.galleries);
	}

	/**
	 * Create meshes
	 */
	createMeshes() {
		for (const hero of this.heroes) {
			this.glHeroes.push(new Hero({
				...hero,
				geometry: this.geometry,
				isPreload: this.isPreload
			}));
		}

		for (const split of this.splits) {
			this.glSplits.push(new Split({
				...split,
				geometry: this.geometry,
				isPreload: this.isPreload
			}));
		}

		for (const slider of this.sliders) {
			if (slider.type === this.types.splitSlider) {
				this.glSplitSliders.push(new SplitSlider({
					...slider,
					geometry: this.geometry,
					isPreload: this.isPreload
				}));
			}
		}

		for (const gallery of this.galleries) {
			if (gallery.type === this.types.gallery) {
				this.glGalleries.push(new Gallery({
					...gallery,
					geometry: this.geometry
				}));
			}
		}
	}

	mapTexture(image) {
		for (const module of this.modules) {
			if (module.textures && module.textures.length) {
				for (const texture of module.textures) {
					if (texture.uid === image.uid
						&& texture.parentUid === image.parentUid
						&& texture.parentType === image.type) {
						return texture;
					}
				}
			}
		}
	}

	show() {
		for (const hero of this.glHeroes) {
			this.scene.add(hero);
			hero.show();
		}

		for (const split of this.glSplits) {
			this.scene.add(split);
			split.show();
		}

		for (const splitSlider of this.glSplitSliders) {
			this.scene.add(splitSlider);
			splitSlider.show();
		}

		for (const gallery of this.glGalleries) {
			this.scene.add(gallery);
			gallery.show();
		}
	}

	hide(isTop) {
		const promises = [];

		if (this.glHeroes && this.glHeroes.length) {
			for (let hero of this.glHeroes) {
				const promise = hero.hide(() => {
					this.scene.remove(hero);
					hero.destroy();
					hero = null;
				}, isTop);
				promises.push(promise);
			}
		}

		if (this.glSplits && this.glSplits.length) {
			for (let split of this.glSplits) {
				const promise = split.hide(() => {
					this.scene.remove(split);
					split.destroy();
					split = null;
				});
				promises.push(promise);
			}
		}

		if (this.glSplitSliders && this.glSplitSliders.length) {
			for (let slider of this.glSplitSliders) {
				const promise = slider.hide(() => {
					this.scene.remove(slider);
					slider.destroy();
					slider = null;
				});
				promises.push(promise);
			}
		}

		if (this.glGalleries && this.glGalleries.length) {
			for (let gallery of this.glGalleries) {
				const promise = gallery.hide(() => {
					this.scene.remove(gallery);
					gallery.destroy();
					gallery = null;
				});
				promises.push(promise);
			}
		}

		return Promise.all(promises);
	}

	/**
	 * Resize
	 */

	resize({ sizes }) {
		this.sizes = sizes;

		if (this.glHeroes && this.glHeroes.length) {
			this.resizeMeshes(this.glHeroes);
		}

		if (this.glSplits && this.glSplits.length) {
			this.resizeMeshes(this.glSplits);
		}

		if (this.glSplitSliders && this.glSplitSliders.length) {
			this.resizeMeshes(this.glSplitSliders);
		}

		if (this.glGalleries && this.glGalleries.length) {
			this.resizeMeshes(this.glGalleries);
		}
	}

	resizeMeshes(meshes) {
		for (const mesh of meshes) {
			// TODO: Loop through all asset bounds
			const { image } = mesh.assets[0];
			const { ref, cache } = image;
			const offsets = getRootOffset(cache);

			image.bounds = {
				imageWidth: ref.offsetWidth,
				imageHeight: ref.offsetHeight,
				width: cache.offsetWidth,
				height: cache.offsetHeight,
				top: offsets.top,
				left: offsets.left
			};

			mesh.resize({
				sizes: this.sizes,
				bounds: image.bounds
			});
		}
	}

	/**
	 * Set.
	 */
	set() {
		for (const hero of this.glHeroes) {
			if (hero && hero.update) {
				hero.update(this.scroll);
			}
		}
	}

	/**
	 * Update.
	 */
	update(scroll) {
		this.scroll = scroll;

		for (let i = 0; i < this.glHeroes.length; i++) {
			const hero = this.glHeroes[i];
			if (hero && hero.update) {
				hero.update(this.scroll);
			}
		}

		for (let i = 0; i < this.glSplits.length; i++) {
			const split = this.glSplits[i];
			if (split && split.update) {
				split.update(this.scroll);
			}
		}

		for (let i = 0; i < this.glSplitSliders.length; i++) {
			const splitSlider = this.glSplitSliders[i];
			if (splitSlider && splitSlider.update) {
				splitSlider.update(this.scroll);
			}
		}

		for (let i = 0; i < this.glGalleries.length; i++) {
			const gallery = this.glGalleries[i];
			if (gallery && gallery.update) {
				gallery.update(this.scroll);
			}
		}
	}

}
